import { ExportOutlined } from '@ant-design/icons';
import { Button } from '@app/components/common/buttons/Button/Button';
import { FONT_WEIGHT, BREAKPOINTS } from '@app/styles/themes/constants';
import { Page } from 'react-pdf';
import styled from 'styled-components';

/**
 * Holds a document's height before its PDF renders. Without it every
 * not-yet-rendered panel would collapse to nothing, stack up at the top of the
 * page and all report themselves in view at once — which is what the deferred
 * render exists to avoid.
 */
export const QuoteContainer = styled.div<{ $awaitingView?: boolean }>`
  ${({ $awaitingView }) => $awaitingView && 'min-height: 420px;'}
`;

export const PageContainer = styled(Page)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  margin-right: auto;
  // flex-wrap: wrap;

  .ant-btn.ant-btn-primary {
    background: none;
    background-color: #1a295a;
    color: white;
    border-radius: 8px;
    cursor: pointer;
    font-size: 16px;
    height: 36px;
    min-width: 125px;
    width: auto;
    padding: 0 16px;
    margin-block: auto;
    font-weight: 500;
    &:hover {
      background-color: #1a295a;
      opacity: 0.9;
    }
    &:disabled {
      background-color: #1a295a;
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  @media (max-width: ${BREAKPOINTS.md - 0.02}px) {
    // flex-wrap: none;
    .ant-btn.ant-btn-primary {
      min-width: 94px;
      font-size: 14px;
    }
  }

  @media (max-width: 620px) {
    gap: 4px;

    .ant-btn.ant-btn-primary {
      max-width: 110px;
    }
  }
`;

export const DocumentIcon = styled(ExportOutlined)`
  font-weight: ${FONT_WEIGHT.extremeBold};
  color: #1a295a;
  font-size: 22px;
  padding-left: 0px;
  position: absolute;
  right: unset;
  left: 80px;

  @media (max-width: ${BREAKPOINTS.md - 0.02}px) {
    position: unset;
    left: unset;
    font-size: 18px;
  }
`;

/** Sits where DocumentIcon used to, keeping the control in the same spot. */
export const DocumentLink = styled.span`
  position: absolute;
  right: unset;
  left: 80px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  color: #1a295a;
  font-size: 16px;
  font-weight: ${FONT_WEIGHT.semibold};
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  @media (max-width: ${BREAKPOINTS.md - 0.02}px) {
    position: unset;
    left: unset;
    font-size: 14px;
  }
`;

export const SignButton = styled(Button)`
  position: absolute;
  left: unset;
  right: 60px;
  background: none;
  background-color: #1a295a;
  color: white;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  height: 36px;

  &:disabled {
    background-color: #1a295a;
    cursor: not-allowed;
  }
  &:hover {
    background-color: #1a295a;
    opacity: 0.9;
  }
  &:focus {
    background-color: #1a295a;
  }

  @media (max-width: ${BREAKPOINTS.md - 0.02}px) {
    position: unset;
    left: unset;
  }
`;

export const ErrorMessage = styled.div`
  color: red;
  text-align: center;
  font-weight: bold;
  font-size: 14px;
`;

export const LoadingMessage = styled.div`
  text-align: center;
  font-size: 16px;
  color: var(--text-secondary-color);
  margin-top: 20px;
  font-weight: 500;
  animation: blink 1.5s infinite;
  @keyframes blink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

export const ButtonText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-main-color);
  user-select: none;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: var(--button-bg-color);
  border: 1px solid var(--button-border-color);
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s;
  &:hover {
    background-color: var(--button-hover-bg-color);
    color: var(--button-hover-text-color);
  }
  &:disabled {
    background-color: var(--button-disabled-bg-color);
    color: var(--button-disabled-text-color);
    cursor: not-allowed;
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--button-focus-ring-color);
  }

  @media (max-width: ${BREAKPOINTS.md - 0.02}px) {
    font-size: 12px;
    padding-inline: 0;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
    max-width: 80px;
  }

  @media (max-width: 620px) {
    max-width: 95px;
  }
`;

export const FooterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;

  &.top {
    margin-top: 17px;
    margin-bottom: 1rem;
  }

  &.sign-button {
    border-top: 1px solid #e9e9e7;
    padding-top: 16px;
    justify-content: center;

    &.only-sign {
      border: none;
      padding-top: 0;
    }

    .ant-btn.ant-btn-primary,
    ${SignButton} {
      margin-left: 0 !important;
      margin-right: 0 !important;
      position: unset !important;
      left: unset !important;
      right: unset !important;
    }
  }

  @media (max-width: ${BREAKPOINTS.md - 0.02}px) {
    flex-direction: column;
    gap: 12px;
  }

  @media (max-width: 620px) {
    gap: 8px;
  }
`;

export const ImagePreview = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  margin-bottom: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

export const FileName = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-main-color);
  user-select: none;
  padding: 4px 8px;

  @media (max-width: ${BREAKPOINTS.md - 0.02}px) {
    font-size: 14px;
    max-width: 80%;
  }

  @media (max-width: 620px) {
    max-width: 70%;
  }
`;
